import React, { useEffect, useState } from 'react';
import {
  Button, Grid, MenuItem, TextField, Typography, CircularProgress
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { updateDesignation } from 'src/services/DesignationService';
import { getAllDepartments } from 'src/services/employeeService';
import { IDesignations } from 'src/content/Designation/interface/designation';
import { ETierOptions } from 'src/content/Designation/enum/ETierOptions';
import { ETrackOptions } from 'src/content/Designation/enum/ETrackOptions';
import SnackBar from 'src/components/SnackBar/SnackBar';

type EditDesignationProps = {
  onClose: () => void;
  onSubmit: (data: IDesignations) => void;
  data: IDesignations;
};

interface Department {
  id: number;
  name: string;
}

const trackOptions = [
  ETrackOptions.TECHNOLOGY,
  ETrackOptions.BUSINESS_CONSULTING,
  ETrackOptions.SHARED_SERVICE,
  ETrackOptions.QUALITY_ASSURANCE,
  ETrackOptions.PROJECT_MANAGEMENT,
] as const;

const tierOptions = [
  ETierOptions.T1,
  ETierOptions.T2,
  ETierOptions.T3,
  ETierOptions.T4,
  ETierOptions.T5,
] as const;

const EditDesignation: React.FC<EditDesignationProps> = ({ onClose, onSubmit, data }) => {
  const [submitting, setSubmitting] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<IDesignations>({
    defaultValues: data,
  });

  // Fetch departments on component mount
  useEffect(() => {
    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      try {
        const response = await getAllDepartments();
        setDepartments(response.data || []);
      } catch (error) {
        console.error('Error fetching departments:', error);
        setSnackBarMessage('Failed to load departments.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, []);

  useEffect(() => {
    setValue('name', data.name);
    setValue('code', data.code);
    setValue('tier', data.tier);
    setValue('track', data.track);
    setValue('departmentId', data.departmentId);
  }, [data, setValue]);

  const onFormSubmit = async (formData: IDesignations) => {
    const trimmedName = formData.name.trim();
    const trimmedCode = formData.code.trim();

    setSubmitting(true);

    try {
      const updatedData = {
        ...formData,
        id: data.id,
        name: trimmedName,
        code: trimmedCode,
        departmentId: formData.departmentId
      };
      const res = await updateDesignation(updatedData);
      if (res.status === 200) {
        onSubmit(updatedData);
        reset();
        onClose();
       }
       else{
        setSnackBarMessage('Failed to update designation. Please try again.');
        setSnackbarSeverity('Error');
        setSnackBarOpen(true);
       }
       setSubmitting(false);
    } catch (error) {
    const apiMessage =
      error.response?.data?.message ? error.response.data.message :
      'An error occurred while updating designation.';
      setSnackBarMessage(apiMessage);
      setSnackbarSeverity('Error');
      setSnackBarOpen(true);
      setSubmitting(false);
  }
};

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Grid container spacing={2} sx={{ p: 3 }}>
        <Grid item xs={12}>
          <Typography fontWeight="bold" fontSize="16px">Edit Designation</Typography>
        </Grid>

        {/* Name */}
        <Grid item xs={6}>
          <Typography sx={{ pl: 1, fontSize: '13px' }}>
            Name<span style={{ color: 'red' }}> *</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Name"
            {...register('name', {
              required: 'Name is required.',
              validate: value => value.trim() !== '' || 'Name cannot be empty or spaces only.'
            })}
          />
          {errors.name && (
            <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
              {errors.name.message}
            </Typography>
          )}
        </Grid>

        {/* Code */}
        <Grid item xs={6}>
          <Typography sx={{ pl: 1, fontSize: '13px' }}>
            Code<span style={{ color: 'red' }}> *</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter Code"
            {...register('code', {
              required: 'Code is required.',
              validate: value => value.trim() !== '' || 'Code cannot be empty or spaces only.'
            })}
          />
          {errors.code && (
            <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
              {errors.code.message}
            </Typography>
          )}
        </Grid>

        {/* Department */}
        <Grid item xs={6}>
          <Typography sx={{ pl: 1, fontSize: '13px' }}>
            Department<span style={{ color: 'red' }}> *</span>
          </Typography>
          <Controller
            name="departmentId"
            control={control as any}
            rules={{ required: 'Department is required.' }}
            render={({ field, fieldState }) => (
              <>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={field.value || ''}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  disabled={loadingDepartments}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) return 'Select Department';
                      const dept = departments.find(d => d.id === selected);
                      return dept ? dept.name : String(selected);
                    }
                  }}
                >
                  {loadingDepartments ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> Loading...
                    </MenuItem>
                  ) : (
                    departments.map((department) => (
                      <MenuItem key={department.id} value={department.id}>
                        {department.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
                {fieldState.error && (
                  <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
        </Grid>

        {/* Track */}
        <Grid item xs={6}>
          <Typography sx={{ pl: 1, fontSize: '13px' }}>
            Track<span style={{ color: 'red' }}> *</span>
          </Typography>
          <Controller
            name="track"
            control={control as any}
            rules={{ required: 'Track is required.' }}
            render={({ field, fieldState }) => (
              <>
                <TextField select fullWidth size="small" {...field}>
                  {trackOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
                {fieldState.error && (
                  <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
        </Grid>

        {/* Tier */}
        <Grid item xs={6}>
          <Typography sx={{ pl: 1, fontSize: '13px' }}>
            Tier<span style={{ color: 'red' }}> *</span>
          </Typography>
          <Controller
            name="tier"
            control={control as any}
            rules={{ required: 'Tier is required.' }}
            render={({ field, fieldState }) => (
              <>
                <TextField select fullWidth size="small" {...field}>
                  {tierOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
                {fieldState.error && (
                  <Typography sx={{ pl: 1, fontSize: '11px' }} color="red">
                    {fieldState.error.message}
                  </Typography>
                )}
              </>
            )}
          />
        </Grid>

        {/* Buttons */}
        <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} sx={{ mr: 2 }} variant="outlined" color="secondary" disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </Grid>
      </Grid>
      <SnackBar
            snackBarOpen={snackBarOpen}
            setSnackBarOpen={setSnackBarOpen}
            snackBarMessage={snackBarMessage}
            snackbarSeverity={snackbarSeverity}
          />
    </form>
  );
};

export default EditDesignation;